import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaStar, FaThumbsUp, FaClock, FaCheck } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';

// Basic styled components
const ReviewsContainer = styled.div`padding: 20px 0;`;
const ErrorMessage = styled.div`background: #f8d7da; color: #721c24; padding: 12px; border-radius: 6px; margin-bottom: 20px;`;
const SuccessMessage = styled.div`background: #d4edda; color: #155724; padding: 12px; border-radius: 6px; margin-bottom: 20px;`;
const LoadingSpinner = styled.div`text-align: center; padding: 40px 20px; color: #666;`;

const RatingSummary = styled.div`
  display: flex; align-items: center; gap: 30px; margin-bottom: 20px;
  @media (max-width: 768px) { flex-direction: column; align-items: flex-start; gap: 20px; }
`;

const OverallRating = styled.div`display: flex; flex-direction: column; align-items: center; gap: 8px;`;
const RatingNumber = styled.div`font-size: 2.5rem; font-weight: 700; color: #333;`;
const StarDisplay = styled.div`display: flex; gap: 4px; font-size: 1.5rem; color: #FFD700;`;
const ReviewCount = styled.div`color: #666; font-size: 14px;`;

const RatingDistribution = styled.div`flex: 1; max-width: 400px;`;
const RatingRow = styled.div`display: flex; align-items: center; gap: 10px; margin-bottom: 6px;`;
const RatingLabel = styled.span`font-size: 14px; color: #666; min-width: 60px;`;
const RatingBar = styled.div`flex: 1; height: 8px; background: #eee; border-radius: 4px; overflow: hidden;`;
const RatingFill = styled.div`height: 100%; background: #FFD700; width: ${props => props.percentage}%; transition: width 0.3s;`;
const RatingCount = styled.span`font-size: 14px; color: #666; min-width: 30px; text-align: right;`;

const ActionsRow = styled.div`display: flex; gap: 12px; align-items: center; margin-top: 20px;`;

const WriteReviewButton = styled.button`
  background: #ff6b35; color: white; border: none; padding: 12px 24px; border-radius: 8px;
  font-size: 16px; font-weight: 600; cursor: pointer; transition: background-color 0.3s;
  display: flex; align-items: center; gap: 8px;
  &:hover { background: #e55a2b; }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;



const ReviewForm = styled.div`
  background: white; border: 1px solid #e9ecef; border-radius: 12px;
  padding: 24px; margin-bottom: 30px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h3`margin: 0 0 20px 0; color: #333; font-size: 18px; font-weight: 600;`;

const StarRating = styled.div`display: flex; gap: 4px; margin-bottom: 16px;`;
const StarButton = styled.button`
  background: none; border: none; font-size: 2rem; 
  color: ${props => props.$filled ? '#FFD700' : '#ddd'};
  cursor: pointer; transition: color 0.2s; padding: 4px;
  &:hover { color: #FFD700; }
`;

const ReviewTextarea = styled.textarea`
  width: 100%; min-height: 120px; padding: 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 14px; font-family: inherit; resize: vertical; margin-bottom: 16px;
  &:focus { outline: none; border-color: #ff6b35; box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2); }
`;

const FormActions = styled.div`display: flex; gap: 12px; justify-content: flex-end;`;
const CancelButton = styled.button`
  background: #6c757d; color: white; border: none; padding: 10px 20px;
  border-radius: 6px; font-size: 14px; cursor: pointer; transition: background-color 0.3s;
  &:hover { background: #5a6268; }
`;
const SubmitButton = styled.button`
  background: #ff6b35; color: white; border: none; padding: 10px 20px;
  border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background-color 0.3s;
  &:hover { background: #e55a2b; }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;

const ReviewsList = styled.div`display: flex; flex-direction: column; gap: 20px;`;
const NoReviews = styled.div`text-align: center; padding: 40px 20px; color: #666; font-size: 16px;`;

const ReviewItem = styled.div`
  background: white; border: 1px solid #e9ecef; border-radius: 12px;
  padding: 20px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ReviewHeader = styled.div`display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;`;
const ReviewerInfo = styled.div`display: flex; align-items: center; gap: 12px;`;
const ReviewerAvatar = styled.div`
  width: 40px; height: 40px; border-radius: 50%; background: #ff6b35; color: white;
  display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px;
`;

const ReviewerDetails = styled.div`flex: 1;`;
const ReviewerName = styled.div`
  font-weight: 600; color: #333; font-size: 14px; margin-bottom: 4px;
  display: flex; align-items: center; gap: 8px;
`;

const VerifiedBadge = styled.span`
  background: #28a745; color: white; font-size: 10px; padding: 2px 6px;
  border-radius: 10px; font-weight: 500;
`;

const ReviewDate = styled.div`color: #666; font-size: 12px; display: flex; align-items: center; gap: 4px;`;
const ReviewRating = styled.div`display: flex; gap: 2px; font-size: 1rem; color: #FFD700;`;
const ReviewText = styled.div`color: #333; font-size: 14px; line-height: 1.6; margin-bottom: 16px;`;

const AdminResponse = styled.div`
  background: #f8f9fa; border-left: 4px solid #ff6b35; padding: 16px;
  border-radius: 0 8px 8px 0; margin-top: 12px;
`;

const AdminResponseHeader = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
  font-size: 12px; color: #666; font-weight: 600;
`;

const AdminResponseText = styled.div`color: #333; font-size: 14px; line-height: 1.5;`;

const ReviewActions = styled.div`display: flex; gap: 16px; align-items: center;`;
const HelpfulButton = styled.button`
  background: none; border: none; color: #666; font-size: 14px; cursor: pointer;
  display: flex; align-items: center; gap: 6px; padding: 6px 12px;
  border-radius: 6px; transition: all 0.3s;
  &:hover { background: #f8f9fa; color: #ff6b35; }
  &:disabled { cursor: not-allowed; }
`;

const ProductReviews = ({ productId }) => {
  const { isAuthenticated: isAuth } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [, setReviewEligibility] = useState(null);

  // Загрузка отзывов
  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/products/${productId}/reviews`);
      if (response.success) {
        setReviews(response.data.reviews);
      }
    } catch (err) {
      setError('Ошибка при загрузке отзывов');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Загрузка статистики отзывов
  const loadReviewsStats = useCallback(async () => {
    try {
      const response = await apiRequest(`/products/${productId}/reviews/stats`);
      if (response.success) {
        setReviewsStats(response.data);
      }
    } catch (err) {
      console.error('Error loading reviews stats:', err);
    }
  }, [productId]);

  // Проверка возможности оставить отзыв
  const checkReviewEligibility = useCallback(async () => {
    if (!isAuth()) return;
    try {
      const response = await apiRequest(`/products/${productId}/reviews/can-review`);
      if (response.success) {
        setReviewEligibility(response.data);
        setCanReview(response.data.canReview);
      }
    } catch (err) {
      console.error('Error checking review eligibility:', err);
    }
  }, [productId, isAuth]);

  useEffect(() => {
    loadReviews();
    loadReviewsStats();
    checkReviewEligibility();
  }, [loadReviews, loadReviewsStats, checkReviewEligibility]);



  // Отправка отзыва
  const handleSubmitReview = async () => {
    if (!rating || !reviewText.trim()) {
      setError('Пожалуйста, выберите оценку и напишите отзыв');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await apiRequest(`/products/${productId}/reviews`, {
        method: 'POST',
        data: { rating, reviewText: reviewText.trim() }
      });

      if (response.success) {
        setMessage('Отзыв отправлен на модерацию. Спасибо!');
        setRating(0);
        setReviewText('');
        setShowForm(false);
        setCanReview(false);
        loadReviewsStats();
      } else {
        setError(response.message || 'Ошибка при отправке отзыва');
      }
    } catch (err) {
      setError('Ошибка при отправке отзыва');
    } finally {
      setSubmitting(false);
    }
  };

  // Отметка отзыва как полезного
  const handleMarkHelpful = async (reviewId) => {
    if (!isAuth()) {
      setError('Войдите в систему, чтобы оценить отзыв');
      return;
    }

    try {
      const response = await apiRequest(`/reviews/${reviewId}/helpful`, {
        method: 'POST',
        data: { isHelpful: true }
      });

      if (response.success) {
        setMessage('Спасибо за оценку!');
        loadReviews();
      } else {
        setError(response.message || 'Ошибка при оценке отзыва');
      }
    } catch (err) {
      setError('Ошибка при оценке отзыва');
    }
  };

  // Рендер звезд
  const renderStars = (rating, interactive = false, onClick = null) => {
    return Array.from({ length: 5 }, (_, index) => {
      const filled = index < rating;
      return interactive ? (
        <StarButton key={index} $filled={filled} onClick={() => onClick && onClick(index + 1)}>★</StarButton>
      ) : (
        <span key={index} style={{ color: filled ? '#FFD700' : '#ddd' }}>★</span>
      );
    });
  };

  // Получение инициалов пользователя
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  if (loading && reviews.length === 0) {
    return <LoadingSpinner>Загрузка отзывов...</LoadingSpinner>;
  }

  return (
    <ReviewsContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {message && <SuccessMessage>{message}</SuccessMessage>}

      {/* Сводка рейтинга */}
      {reviewsStats && (
        <>
          <RatingSummary>
            <OverallRating>
              <RatingNumber>
                {reviewsStats.averageRating > 0 ? reviewsStats.averageRating.toFixed(1) : '0.0'}
              </RatingNumber>
              <StarDisplay>{renderStars(Math.round(reviewsStats.averageRating))}</StarDisplay>
              <ReviewCount>
                На основе {reviewsStats.totalReviews} отзыв{reviewsStats.totalReviews === 1 ? 'а' : 'ов'}
              </ReviewCount>
            </OverallRating>
            
            <RatingDistribution>
              {[5, 4, 3, 2, 1].map(stars => {
                const count = reviewsStats.distribution[stars] || 0;
                const percentage = reviewsStats.totalReviews > 0 ? (count / reviewsStats.totalReviews) * 100 : 0;
                return (
                  <RatingRow key={stars}>
                    <RatingLabel>{stars} звезд</RatingLabel>
                    <RatingBar><RatingFill percentage={percentage} /></RatingBar>
                    <RatingCount>{count}</RatingCount>
                  </RatingRow>
                );
              })}
            </RatingDistribution>
          </RatingSummary>

          <ActionsRow>
            {isAuth() ? (
              canReview ? (
                <WriteReviewButton onClick={() => setShowForm(true)} disabled={showForm}>
                  <FaStar />Написать отзыв
                </WriteReviewButton>
              ) : (
                <WriteReviewButton disabled>Вы уже оставили отзыв</WriteReviewButton>
              )
            ) : (
              <WriteReviewButton disabled>Войдите, чтобы оставить отзыв</WriteReviewButton>
            )}
          </ActionsRow>
        </>
      )}

      {/* Форма отзыва */}
      {showForm && (
        <ReviewForm>
          <FormTitle>Оставьте свой отзыв</FormTitle>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Ваша оценка:
            </label>
            <StarRating>{renderStars(rating, true, setRating)}</StarRating>
          </div>
          <ReviewTextarea
            placeholder="Поделитесь впечатлением о товаре..."
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            maxLength={2000}
          />
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            {reviewText.length}/2000 символов
          </div>
          <FormActions>
            <CancelButton onClick={() => setShowForm(false)}>Отмена</CancelButton>
            <SubmitButton 
              onClick={handleSubmitReview}
              disabled={submitting || !rating || !reviewText.trim()}
            >
              {submitting ? 'Отправка...' : 'Отправить отзыв'}
            </SubmitButton>
          </FormActions>
        </ReviewForm>
      )}

      {/* Список отзывов */}
      <ReviewsList>
        {reviews.length === 0 ? (
          <NoReviews>Пока нет отзывов о этом товаре. Будьте первым!</NoReviews>
        ) : (
          reviews.map((review) => (
            <ReviewItem key={review.id}>
              <ReviewHeader>
                <ReviewerInfo>
                  <ReviewerAvatar>{getUserInitials(review.display_name || review.name)}</ReviewerAvatar>
                  <ReviewerDetails>
                    <ReviewerName>
                      {review.display_name || review.name || 'Покупатель'}
                      {review.is_verified_purchase && (
                        <VerifiedBadge>Проверенный покупатель</VerifiedBadge>
                      )}
                    </ReviewerName>
                    <ReviewDate>
                      <FaClock />{formatDate(review.created_at)}
                    </ReviewDate>
                  </ReviewerDetails>
                </ReviewerInfo>
                <ReviewRating>{renderStars(review.rating)}</ReviewRating>
              </ReviewHeader>

              <ReviewText>{review.review_text || review.content}</ReviewText>

              {/* Ответ администратора */}
              {review.admin_response && (
                <AdminResponse>
                  <AdminResponseHeader>
                    <FaCheck style={{ color: '#28a745' }} />
                    <strong>Ответ магазина</strong>
                    {review.admin_response_date && (
                      <><FaClock />{formatDate(review.admin_response_date)}</>
                    )}
                  </AdminResponseHeader>
                  <AdminResponseText>{review.admin_response}</AdminResponseText>
                </AdminResponse>
              )}

              <ReviewActions>
                <HelpfulButton 
                  onClick={() => handleMarkHelpful(review.id)}
                  disabled={!isAuth()}
                >
                  <FaThumbsUp />Полезно ({review.helpfulness_count || 0})
                </HelpfulButton>
              </ReviewActions>
            </ReviewItem>
          ))
        )}
      </ReviewsList>
    </ReviewsContainer>
  );
};

export default ProductReviews;